import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookingDataStatus } from '../../BookingWidget/BookingManagment/bookingManagment.interface';
import { BookingDataContext } from '../../../context/bookingDataContext/bookingDataContext';
import { ITransactionResponse } from '../../shared/braintree/payments';
import { BookingContext } from '../../../context/bookingContext/bookingContext';
import usePolicyDataService from '../../../shared/services/policyData.service';
import { BookingQuoteContext } from '../../../context/quoteContext/quoteContext';
import { LoadingContext } from '../../../context/loadingContext/loadingContext';
import { NotificationContext } from '../../../shared/components/Notification/NotificationContext';
import { getQuoteId } from '../../../shared/helpers/quote.helper';
import { BookingStepValueEnum } from '../../BookingWidget/bookingSteps.interface';
import { Policy } from '../../../shared/interfaces/policyResponse.interface';
import authHelper from '../../../shared/helpers/auth.helper';
import { cm360ErrorCodes } from '../../../shared/helpers/axiosErrorToTranslationKey.helper';
import usePolicyPriceDiff from './usePolicyPriceDiff';
import { BookingQuoteResponse } from '../../BookingWidget/BookingQuoteManagment/bookingQuoteResponse.interface';
import useBookingDataService from '../../../shared/services/bookingData.service';
import { BookingConfigContext } from '../../../context/bookingConfigContext/bookingConfigContext';
import { useSessionContext } from '../../../context/sessionContext/sessionContext';

interface SummaryBlockData {
  label: string;
  subLabel: string;
  data: string;
}

interface UseFlowSpecificFactory {
  policySaveMethod: (
    transactionResponse: ITransactionResponse,
  ) => Promise<Policy>;
  isPolicyModificationFlow: boolean;
  shouldUsePaymentHome: boolean;
  handlePaymentLink: () => Promise<void>;
  shouldUsePayment: boolean;
  isPaymentLinkSent: boolean;
  recapDeclarationsHeader: string;
  submitActionLabel: string;
  summaryBlockData: SummaryBlockData;
  getQuoteId: (bookingQuoteResponse: BookingQuoteResponse) => string;
  setIsPaymentStausLoading: (isLoading: boolean) => void;
}

const useFlowSpecificFactory = (
  nextStep?: (step?: number) => void,
): UseFlowSpecificFactory => {
  const { bookingSteps, bookingReferenceId, update, setCurrentStep } =
    useContext(BookingContext);
  const { bookingQuoteResponse } = useContext(BookingQuoteContext);
  const { bookingConfigData } = useContext(BookingConfigContext);
  const { bookingDataResponse, reloadBooking } = useContext(BookingDataContext);
  const { registerPolicy, modifyPolicy, sendPaymentHome, cancelPaymentHome } =
    usePolicyDataService();
  const { sessionParams } = useSessionContext();
  const { t } = useTranslation();
  const { setIsLoading } = useContext(LoadingContext);
  const { showNotification } = useContext(NotificationContext);
  const { priceDiff } = usePolicyPriceDiff();
  const { isAgent, availablePaymentMethods } = authHelper();
  const { getBookingPaymentStatus } = useBookingDataService(
    bookingConfigData,
    sessionParams,
  );
  const [isPaymentStatusLoading, setIsPaymentStausLoading] = useState(false);
  const isPolicyModificationFlow = useMemo(
    () =>
      bookingDataResponse?.bookingData.status === BookingDataStatus.RECREATED ||
      (!!bookingDataResponse?.bookingData.prevBookingId &&
        [
          BookingDataStatus.IN_PROGRESS,
          BookingDataStatus.PENDING_PAYMENT,
        ].includes(
          bookingDataResponse?.bookingData.status as BookingDataStatus,
        )),
    [bookingDataResponse],
  );

  const shouldUsePayment = useMemo(() => {
    if (!bookingDataResponse?.bookingData) {
      return false;
    }

    const isPolicyWithNegativeOrZeroPriceDiff =
      isPolicyModificationFlow && priceDiff <= 0;

    if (
      !isPolicyWithNegativeOrZeroPriceDiff &&
      bookingDataResponse.bookingData.paymentMethod === 'credit_card'
    ) {
      return true;
    }

    const isOnAccountPayment =
      bookingDataResponse.bookingData.paymentMethod === 'on_account' ||
      availablePaymentMethods().includes('on_account');

    return !isOnAccountPayment && !isPolicyWithNegativeOrZeroPriceDiff;
  }, [
    availablePaymentMethods,
    bookingDataResponse,
    isPolicyModificationFlow,
    priceDiff,
  ]);

  const isPaymentLinkSent = useMemo(
    () =>
      bookingDataResponse?.bookingData.status ===
      BookingDataStatus.PENDING_PAYMENT,
    [bookingDataResponse],
  );

  const shouldUsePaymentHome = useMemo(() => {
    if (!bookingDataResponse?.bookingData) {
      return false;
    }

    const isIndepCatalog =
      bookingDataResponse.bookingData.catalog.includes('INDEP');

    return (
      isAgent() && ((shouldUsePayment && isIndepCatalog) || isPaymentLinkSent)
    );
  }, [bookingDataResponse, isAgent, isPaymentLinkSent, shouldUsePayment]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    const checkPaymentStatus = async (): Promise<void> => {
      if (bookingDataResponse?.bookingData?.id) {
        try {
          const response = await getBookingPaymentStatus(
            bookingDataResponse.bookingData.id,
          );
          if (response.status === 'done' || response.status === 'expired') {
            clearInterval(intervalId);
            setIsPaymentStausLoading(false);

            if (response.status === 'done') {
              showNotification('paymentDone', 'success', false);
              update(
                { policyId: response.policyId },
                BookingStepValueEnum.QuotationRecapDeclaration,
                undefined,
                false,
              );
              if (nextStep) {
                nextStep();
              }
            } else {
              showNotification('paymentExpired', 'error', false);
              reloadBooking();
            }
          }
        } catch (error) {
          clearInterval(intervalId);
          setIsPaymentStausLoading(false);
          showNotification('fetchingData', 'error', false);
        }
      }
    };

    if (isAgent() && isPaymentStatusLoading && isPaymentLinkSent) {
      checkPaymentStatus();
      intervalId = setInterval(checkPaymentStatus, 10000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isAgent, isPaymentStatusLoading, bookingDataResponse, update, nextStep]);

  const handleSendPaymentHome = useCallback(async (): Promise<void> => {
    try {
      if (bookingDataResponse) {
        await sendPaymentHome(bookingDataResponse);
        setIsPaymentStausLoading(true);
        showNotification('paymentLinkSent', 'success', false);
      } else {
        showNotification('notFound', 'error', false);
      }
    } catch (error) {
      showNotification('fetchingData', 'error', false);
    }
  }, [bookingDataResponse, sendPaymentHome, showNotification]);

  const handleCancelPaymentHome = useCallback(async (): Promise<void> => {
    try {
      if (bookingDataResponse) {
        await cancelPaymentHome(bookingDataResponse);
        setIsPaymentStausLoading(false);
        showNotification('paymentLinkCancelled', 'success', false);
      } else {
        showNotification('notFound', 'error', false);
      }
    } catch (error) {
      showNotification('fetchingData', 'error', false);
    }
  }, [bookingDataResponse, cancelPaymentHome, showNotification]);

  const handlePaymentLink = useCallback(async (): Promise<void> => {
    if (isPaymentLinkSent) {
      await handleCancelPaymentHome();
    } else {
      await handleSendPaymentHome();
    }
  }, [handleCancelPaymentHome, handleSendPaymentHome, isPaymentLinkSent]);

  const handlePolicyRegistration = useCallback(
    async (transactionResponse: ITransactionResponse): Promise<Policy> => {
      setIsLoading(true);
      try {
        return await registerPolicy(
          bookingDataResponse,
          bookingSteps,
          bookingReferenceId,
        );
      } catch (error) {
        showNotification('fetchingData', 'error', false);
        return Promise.reject();
      } finally {
        setIsLoading(false);
      }
    },
    [
      setIsLoading,
      registerPolicy,
      bookingDataResponse,
      bookingSteps,
      bookingReferenceId,
      showNotification,
    ],
  );

  const handlePolicyModification = useCallback(
    async (transactionResponse: ITransactionResponse): Promise<Policy> => {
      setIsLoading(true);
      try {
        return await modifyPolicy(
          bookingDataResponse,
          bookingSteps,
          bookingReferenceId,
        );
      } catch (e) {
        if (
          e instanceof Error &&
          e.message === cm360ErrorCodes[409].cm409_01.code
        ) {
          showNotification('endDateInPast', 'error', false);
        } else {
          showNotification('fetchingData', 'error', false);
        }
        return Promise.reject();
      } finally {
        setIsLoading(false);
      }
    },
    [bookingDataResponse, bookingSteps, modifyPolicy, bookingReferenceId],
  );

  const policySaveMethod = useMemo(() => {
    if (isPolicyModificationFlow) {
      return handlePolicyModification;
    }

    return handlePolicyRegistration;
  }, [
    handlePolicyModification,
    handlePolicyRegistration,
    handleSendPaymentHome,
    shouldUsePaymentHome,
    isPolicyModificationFlow,
    isPaymentLinkSent,
  ]);

  const recapDeclarationsHeader = useMemo(
    () =>
      isPolicyModificationFlow
        ? t('DECLARATIONS.confirmationHeader')
        : t('DECLARATIONS.header'),
    [isPolicyModificationFlow, t],
  );

  const submitActionLabel = useMemo(() => {
    if (shouldUsePaymentHome) {
      if (isPaymentLinkSent) {
        return t('DECLARATIONS.cancelPayment');
      }
      return t('DECLARATIONS.sendPayment');
    }

    if (isPolicyModificationFlow) {
      return t('DECLARATIONS.confirmChanges');
    }

    return t('DECLARATIONS.issuePolicy');
  }, [isPolicyModificationFlow, shouldUsePaymentHome, t, isPaymentLinkSent]);

  const summaryBlockData = useMemo(
    () =>
      isPolicyModificationFlow
        ? {
            label: t('BOOKING.POLICY_SUMMARY_BLOCK.title'),
            subLabel: t('BOOKING.POLICY_SUMMARY_BLOCK.policyModification'),
            data:
              bookingSteps[BookingStepValueEnum.QuotationRecapDeclaration]
                ?.policyId ??
              bookingDataResponse?.bookingData?.proxy?.policyId ??
              '',
          }
        : {
            label: t('BOOKING.QUOTATION_SUMMARY_BLOCK.title'),
            subLabel: '',
            data:
              bookingSteps?.[BookingStepValueEnum.QuotationRecapDeclaration]
                ?.policyId ?? getQuoteId(bookingQuoteResponse),
          },
    [
      bookingDataResponse?.bookingData?.proxy?.policyId,
      bookingQuoteResponse,
      bookingSteps,
      isPolicyModificationFlow,
    ],
  );

  return {
    isPolicyModificationFlow,
    policySaveMethod,
    recapDeclarationsHeader,
    submitActionLabel,
    summaryBlockData,
    shouldUsePayment,
    shouldUsePaymentHome,
    handlePaymentLink,
    isPaymentLinkSent,
    setIsPaymentStausLoading,
    getQuoteId,
  };
};

export default useFlowSpecificFactory;
